import { css } from "emotion";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  DropdownMenuItemType,
  IDropdownOption,
  IStackProps,
  PrimaryButton,
  ScrollablePane,
  Stack
} from "office-ui-fabric-react";
import React from "react";
import { Field, InjectedFormProps, reduxForm } from "redux-form";
import { DialogProps, Employee, Group, Job } from "../../../types/types";
import { renderDrowdown, renderTextField } from "../../FormFields";

export const jobEditFormId = "JobEditForm";

export interface JobEditDialogProps {
  dialogProps: DialogProps;
  initialValues?: Job;
  groups?: Group[];
  employees?: Employee[];
  submit: (values: any) => void;
  close: () => void;
}

export const JobEditDialog = (props: JobEditDialogProps) => {
  const {
    dialogProps,
    initialValues,
    groups,
    employees,
    submit,
    close
  } = props;

  const groupOptions =
    groups &&
    groups.map(v => ({
      key: v.id,
      text: v.groupName
    }));

  const employeeOptions =
    employees &&
    employees
      .sort((a, b) => a.shozokuSort - b.shozokuSort)
      .reduce<IDropdownOption[]>((acc, cur) => {
        const option = {
          key: cur.kojinCode,
          text: cur.fullName,
          data: cur.groupName
        };

        if (acc.length === 0 || acc[acc.length - 1].data !== cur.groupName) {
          const header = {
            key: `${cur.groupName}header`,
            text: cur.groupName,
            itemType: DropdownMenuItemType.Header
          };
          return acc.concat(header, option);
        }

        return acc.concat(option);
      }, []);

  const Form = (formProps: InjectedFormProps) => {
    const { handleSubmit, pristine, reset, submitting } = formProps;

    const columnProps: Partial<IStackProps> = {
      tokens: { childrenGap: 10 },
      styles: { root: { width: 300 } }
    };

    return (
      <Dialog
        hidden={!dialogProps.isOpen}
        onDismiss={close}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: dialogProps.title
        }}
        modalProps={{
          isBlocking: dialogProps.isModal,
          containerClassName: css`
            height: 80vh;
            max-height: 80vh;
            width: 50vw;
            max-width: 50vw;
          `
        }}
      >
        <form id="jobForm" onSubmit={handleSubmit} noValidate={true}>
          <Field name="id" component="input" type="hidden" />
          <div
            className={css`
              height: 50vh;
            `}
          >
            <ScrollablePane>
              <Stack
                horizontal={true}
                tokens={{ childrenGap: 50 }}
                styles={{ root: {} }}
              >
                <Stack {...columnProps}>
                  <div>
                    <Field
                      name="shozokuCode"
                      label="グループ"
                      tabIndex={1}
                      required={true}
                      component={renderDrowdown({ options: groupOptions })}
                    />
                  </div>
                  <div>
                    <Field
                      name="jobNo"
                      label="ジョブ番"
                      type="text"
                      tabIndex={3}
                      maxLength={6}
                      component={renderTextField}
                    />
                  </div>
                  <div>
                    <Field
                      name="kakudo"
                      label="確度"
                      type="number"
                      tabIndex={5}
                      min={0}
                      max={1}
                      step={0.1}
                      component={renderTextField}
                    />
                  </div>
                  <div>
                    <Field
                      name="hikiai"
                      label="引合"
                      type="number"
                      tabIndex={7}
                      min={0}
                      step={1000}
                      component={renderTextField}
                    />
                  </div>
                </Stack>
                <Stack {...columnProps}>
                  <div>
                    <Field
                      name="clientName"
                      label="客先名"
                      type="text"
                      tabIndex={2}
                      required={true}
                      component={renderTextField}
                    />
                  </div>
                  <div>
                    <Field
                      name="jobName"
                      label="ジョブ名"
                      type="text"
                      tabIndex={4}
                      required={true}
                      component={renderTextField}
                    />
                  </div>
                  <div>
                    <Field
                      name="deliveryDate"
                      label="納期"
                      type="text"
                      tabIndex={6}
                      component={renderTextField}
                    />
                  </div>
                  <div>
                    <Field
                      name="jlEmployeeId"
                      label="JL（ジョブリーダー）"
                      tabIndex={8}
                      component={renderDrowdown({ options: employeeOptions })}
                    />
                  </div>
                </Stack>
              </Stack>
            </ScrollablePane>
          </div>
        </form>
        <DialogFooter>
          <PrimaryButton
            text="登録"
            type="submit"
            form="jobForm"
            tabIndex={100}
            disabled={submitting}
          />
          <DefaultButton
            onClick={reset}
            text="リセット"
            tabIndex={200}
            disabled={pristine || submitting}
          />
          <DefaultButton
            onClick={close}
            text="閉じる"
            tabIndex={300}
            disabled={submitting}
          />
        </DialogFooter>
      </Dialog>
    );
  };

  const ConnectedForm = reduxForm({
    form: jobEditFormId,
    initialValues:
      dialogProps.type === "JOB_UPDATE" ? initialValues : undefined,
    validate: validator,
    destroyOnUnmount: false,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    touchOnBlur: false
  })(Form);
  return <ConnectedForm onSubmit={submit} />;
};

const validator = (values: any) => {
  const errors: any = {};

  if (!values.shozokuCode) {
    errors.shozokuCode = "グループは必須です";
  }
  if (!values.clientName) {
    errors.clientName = "客先名は必須です";
  }
  if (!values.jobName) {
    errors.jobName = "ジョブ名は必須です";
  }

  return errors;
};
